<template>
  <Transition
    enter-active-class="transition-all duration-500 ease-out"
    enter-from-class="translate-y-4 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-4 opacity-0"
  >
    <div
      v-if="showBanner"
      class="fixed bottom-4 left-3 right-3 sm:left-4 sm:right-auto sm:w-[420px] z-[10001] bg-white border border-gray-200 shadow-[0_10px_30px_rgba(0,0,0,0.12)] rounded-2xl"
    >
      <div class="w-full px-4 sm:px-5 py-4">
        <div class="flex flex-col items-start justify-between gap-4">
          <!-- Left: Icon + Text -->
          <div class="flex items-center gap-3 flex-1">
            <!-- Cookie Icon -->
            <div class="flex-shrink-0 mt-0.5">
              <svg
                class="w-5 h-5 text-primary"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M21.598 11.064a1.006 1.006 0 0 0-.854-.172A2.938 2.938 0 0 1 20 11c-1.654 0-3-1.346-3.003-2.938.005-.034.016-.134.017-.168a.998.998 0 0 0-1.254-1.006A3.002 3.002 0 0 1 15 7c-1.654 0-3-1.346-3-3 0-.217.031-.444.099-.716a1 1 0 0 0-1.067-1.236A9.956 9.956 0 0 0 2 12c0 5.514 4.486 10 10 10s10-4.486 10-10c0-.049-.003-.097-.007-.16a1.004 1.004 0 0 0-.395-.776zM8.5 6a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm-2 8a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm3 4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm2.5-6.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm3.5 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"
                />
              </svg>
            </div>

            <!-- Text with link -->
            <div class="flex-1">
              <p
                class="text-sm sm:text-base text-gray-700 leading-relaxed font-outfit"
              >
                <span class="font-medium">Nous utilisons des cookies</span>
                pour améliorer votre expérience sur notre plateforme.
                <NuxtLink
                  to="/politique-de-cookies"
                  class="text-[#8a6b22] hover:text-[#6f541a] underline decoration-1 underline-offset-2 font-medium transition-colors"
                >
                  En savoir plus
                </NuxtLink>
              </p>
            </div>
          </div>

          <!-- Right: Buttons -->
          <div class="flex items-center gap-2 w-full">
            <button
              @click="openPreferences"
              class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors font-outfit"
            >
              Gérer mes préférences
            </button>
          </div>
          <div class="flex items-center gap-2 w-full">
            <button
              @click="handleDecline"
              class="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-primary-light transition-colors font-outfit"
            >
              Refuser
            </button>
            <button
              @click="handleAccept"
              class="flex-1 px-5 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-darkAmber rounded-full shadow-sm transition-all hover:shadow-md font-outfit"
            >
              Accepter
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <Transition
    enter-active-class="transition-all duration-200 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-all duration-150 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showPreferences"
      class="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[1px] flex items-end sm:items-center justify-center p-4"
    >
      <div class="w-full max-w-lg bg-white rounded-2xl shadow-xl p-5">
        <div class="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">
              Préférences de cookies
            </h3>
            <p class="text-sm text-gray-600 mt-1">
              Gérez vos choix de suivi et de publicité.
            </p>
          </div>
          <button
            @click="showPreferences = false"
            class="text-gray-400 hover:text-gray-600"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        <div class="space-y-4">
          <div class="p-3 border border-gray-200 rounded-xl">
            <Checkbox :model-value="true" disabled>
              <div>
                <p class="text-sm font-medium text-gray-900">Essentiels</p>
                <p class="text-xs text-gray-600">
                  Nécessaires au fonctionnement du site.
                </p>
              </div>
            </Checkbox>
          </div>

          <div class="p-3 border border-gray-200 rounded-xl">
            <Checkbox v-model="preferences.analytics">
              <div>
                <p class="text-sm font-medium text-gray-900">
                  Analytics & marketing
                </p>
                <p class="text-xs text-gray-600">
                  GA et Facebook Pixel pour mesurer l'audience et les campagnes.
                </p>
              </div>
            </Checkbox>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-2 mt-5">
          <button
            @click="savePreferences"
            class="flex-1 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-darkAmber rounded-full shadow-sm transition-all"
          >
            Enregistrer
          </button>
          <button
            @click="handleDecline"
            class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
          >
            Tout refuser
          </button>
          <button
            @click="handleAccept"
            class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
          >
            Tout accepter
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import Checkbox from "~/components/ui/form/Checkbox.vue";

const showBanner = ref(false);
const showPreferences = ref(false);
const preferences = reactive({
  analytics: true,
});
const COOKIE_CONSENT_KEY = "tayba-cookie-consent";
const CONSENT_EVENT = "tayba-cookie-consent-changed";

type ConsentPrefs = {
  analytics: boolean;
};

const parseConsent = (): { prefs: ConsentPrefs | null; migrated: boolean } => {
  const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!raw) return { prefs: null, migrated: false };

  if (raw === "accepted" || raw === "declined") {
    return {
      prefs: { analytics: raw === "accepted" },
      migrated: true,
    };
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      prefs: { analytics: !!parsed?.analytics },
      migrated: false,
    };
  } catch (error) {
    return { prefs: null, migrated: false };
  }
};

onMounted(() => {
  // Check if user has already made a choice
  const { prefs, migrated } = parseConsent();

  if (prefs) {
    preferences.analytics = prefs.analytics;
    if (migrated) {
      localStorage.setItem(
        COOKIE_CONSENT_KEY,
        JSON.stringify({ analytics: prefs.analytics, updatedAt: new Date() }),
      );
    }
  }

  // Show banner if no consent has been given yet
  if (!prefs) {
    // Small delay for better UX
    setTimeout(() => {
      showBanner.value = true;
    }, 1000);
  }
});

const setConsent = (prefs: ConsentPrefs) => {
  localStorage.setItem(
    COOKIE_CONSENT_KEY,
    JSON.stringify({ ...prefs, updatedAt: new Date() }),
  );
  window.dispatchEvent(
    new CustomEvent(CONSENT_EVENT, { detail: { value: prefs } }),
  );
  showBanner.value = false;
  showPreferences.value = false;
};

const handleAccept = () => {
  setConsent({ analytics: true });
};

const handleDecline = () => {
  setConsent({ analytics: false });
};

const openPreferences = () => {
  const { prefs } = parseConsent();
  preferences.analytics = prefs?.analytics ?? true;
  showPreferences.value = true;
};

const savePreferences = () => {
  setConsent({ analytics: preferences.analytics });
};
</script>
